<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('devices.productSensorHubs.name') }}
      </v-toolbar-title>
    </template>

    <template #content>
      <v-skeleton-loader
        v-if="loading"
        class="m-auto"
        type="table"
        transition="scale-transition"
      />
      <VTableRemote
        :key="componentKey"
        :iconHeader=" 'add'"
        :items="displayedProductSensorHub"
        :headers="tableHeaders"
        :sortableColumns="sortableColumns"
        :itemActions="itemActions"
        :disablePagination="true"
        :pagination="{}"
        hideDefaultFooter
        :isCustomIcon="true"
        :isInfinitScroll="true"
        :isSearchable="false"
        :title="$t('devices.productSensorHubs.name')"
        :centerColumns="['action']"
        :notSortableColumns="['action']"
        @iconAction="onClickAction"
        @sendQueryParams="fetchQuery"
      >
        <template #SearchWithFilters>
          <SearchWithFilters
            :filters="[]"
            :searchPlaceholder="$t('controls.search')"
            :filterName="filterName"
            @onSearch="handleSearch"
          />
        </template>
        <template #item.connectionType="{ item }">
          <p> {{ $t(`devices.productSensorHubs.connectionType.${item.connectionType}`) }}</p>
        </template>
      </VTableRemote>
    </template>
  </BoxWithTitle>
</template>

<script>
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import Vue from 'vue';
import { mapGetters } from 'vuex';

import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import ProductSensorForm from '@/views/sites/devices/productSensorsHubs/ProductSensorForm.vue';


export default {
    name: 'ProductSensorTab',
    components: {
        VTableRemote,
        BoxWithTitle,
        SearchWithFilters,
    },
    data() {
        return {
            loading: false,
            componentKey: 0,
            productSensorsList: [],
            filterName: 'productSensorHub',
            queryParams: { },
        };
    },
    computed: {
        ...mapGetters([
            'site',
            'hasRole',
            'hasPermission',
            'teamID',
        ]),
        tableHeaders() {
            const headers = [
                {
                    text: this.$t('devices.name'),
                    value: 'name',
                    sortable: false,
                },
                {
                    text: this.$t('devices.make'),
                    value: 'make',
                    sortable: false,
                },
                {
                    text: this.$t('devices.model'),
                    value: 'model',
                    sortable: false,
                },
                {
                    text: this.$t('devices.macAddress'),
                    value: 'macAddress',
                    sortable: false,
                },
                {
                    text: this.$t('devices.connectionType'),
                    value: 'translatedConnectionType',
                    sortable: false,
                },
                {
                    text: this.$t('forms.labels.network'),
                    value: 'network.name',
                    sortable: false,
                },
                {
                    text: this.$t('forms.labels.component'),
                    value: 'component.name',
                    sortable: false,
                },
            ];

            return headers;
        },
        sortableColumns() {
            return this.tableHeaders
                .filter(column => column.sortable)
                .map(column => this.$options.filters.lowercase(column.value));
        },
        displayedProductSensorHub() {
            return this.productSensorsList.map(item => {
                const translatedConnectionType = this.translateConnectionType(item.connectionType);
                return {
                    ...item,
                    translatedConnectionType,
                };
            });
        },
        itemActions() {
            const actions = [];
            if (this.hasPermission('controllers.edit')) {
                actions.push({ name: this.$t('forms.btn.edit'), icon: 'edit' });
            }
            if (this.hasPermission('controllers.remove')) {
                actions.push({ name: this.$t('forms.btn.delete'), icon: 'delete', iconAndTextColor: '#EF4135' });
            }
            return actions;
        },
    },

    async created() {
        await this.getAllProductSensorsHub();
    },

    methods: {
        translateConnectionType(connectionType) {
            const translationKey = `devices.productSensorHubs.connectionType.${connectionType}`;
            return this.$t(translationKey) || connectionType;
        },
        async fetchQuery(queryParams) {
            this.queryParams = {
                search: queryParams.search,
            };
            await this.getAllProductSensorsHub();
        },

        async onClickAction(item, action) {
            const itemId = item.id;
            if (!action && item === 'add') {
                await this.addProductSensorHub();
            }
            if (action) {
                switch (action?.icon) {
                    case 'delete':
                        await this.onClickDeleteProductSensorsHub(itemId);
                        break;
                    case 'edit':
                        await this.editProductSensor(item);
                        break;
                    default:
                        break;
                }
            }
        },
        async reload() {
            this.productSensorsList = [];
            await this.getAllProductSensorsHub();
        },
        handleSearch(search) {
            this.fetchQuery({ search });
        },
        async getAllProductSensorsHub() {
            this.loading = true;
            const { data } = await this.$rq.getAllProductSensorsHub(this.site.id);
            this.productSensorsList = data;
            this.loading = false;
        },
        /* Ajouter un Hub (ProductSensor) depuis le gros bouton rouge "Ajouter" */
        async addProductSensorHub() {
            this.$root.$emit('showModal', {
                title: this.$t('devices.productSensorHubs.add'),
                component: Vue.component('ProductSensorForm', ProductSensorForm),
                payload: { },
                onConfirm: requestPayload => this.onConfirmAddProductSensorHub(requestPayload),
                width: 800,
                isForm: true,
            });
        },
        async onConfirmAddProductSensorHub(requestPayload) {
            await this.$rq.addProductSensorHub(this.site.id, requestPayload);
            this.$toasted.global.toast_success({ message: this.$t('msg.productSensorHubs.added') });
            await this.reload();
        },

        async onClickDeleteProductSensorsHub(itemId) {
            this.$root.$emit('showModal', {
                title: this.$t('devices.productSensorHubs.remove'),
                component: Vue.component('confirmRemove', ConfirmForm),
                payload: { msg: this.$t('devices.productSensorHubs.removeMsg') },
                onConfirm: async () => {
                    await this.$rq.deleteProductSensorsHub(this.site.id, itemId);
                    this.$toasted.global.toast_success({ message: this.$t('msg.productSensorHubs.deleted') });
                    await this.reload();
                },
                isForm: false,
            });
        },
        async editProductSensor(productSensorHub) {
            this.$root.$emit('showModal', {
                title: this.$t('devices.productSensorHubs.update'),
                component: Vue.component('ProductSensorForm', ProductSensorForm),
                payload: { productSensorHub },
                onConfirm: async requestPayload => {
                    if (!requestPayload) {
                        return;
                    }
                    const payload = {
                        ...requestPayload,
                    };

                    const propertiesToRemove = [
                        'active',
                        'alertType',
                        'reading',
                        'id',
                        'minLevelUnits',
                        'minLevel',
                        'maxLevelUnits',
                        'maxLevel',
                        'conversionFactor',
                        'dataSource',
                        'component',
                        'network',
                    ];

                    // Remove the listed properties
                    propertiesToRemove.forEach(prop => {
                        delete payload[prop];
                    });

                    await this.$rq.editProductSensorsHub(this.site.id, productSensorHub.id, payload);
                    this.$toasted.global.toast_success({ message: this.$t('msg.productSensorHubs.updated') });
                    await this.reload();
                },
                width: 800,
                isForm: true,
            });
        },
    },

};

</script>
